import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { SEO } from "@/components/SEO";
import { FadeIn } from "@/components/FadeIn";
import { RevealHeading } from "@/components/RevealHeading";
import { SectionLabel } from "@/components/SectionLabel";
import { industriesDetailed } from "@/data/industries";
import { images } from "@/data/images";

export default function Industries() {
  return (
    <div data-testid="industries-page">
      <SEO
        title="Industries"
        description="DULORME LABS helps restaurants, medical practices, law firms, fitness businesses, professional services and growing companies build stronger brands."
        path="/industries"
      />

      <section className="relative pt-40 pb-24 border-b border-white/10 overflow-hidden">
        <div className="absolute inset-0">
          <img src={images.citySkyline} alt="City skyline at night representing ambitious growing businesses" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#050505]/90 to-[#050505]" />
        </div>
        <div className="container-custom relative z-10">
          <SectionLabel>Industries</SectionLabel>
          <RevealHeading
            tag="h1"
            lines={["BUILT FOR AMBITIOUS", "BUSINESSES."]}
            className="font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.0] tracking-tight text-white max-w-3xl"
          />
        </div>
      </section>

      <section className="py-4">
        <div className="container-custom">
          {industriesDetailed.map((ind, i) => (
            <FadeIn key={ind.title} delay={Math.min(i * 0.04, 0.3)}>
              <div data-testid={`industry-detail-${i}`} className="grid md:grid-cols-12 gap-8 py-14 border-b border-white/10">
                <div className="md:col-span-4">
                  <span className="font-display text-sm text-violet-400">{String(i + 1).padStart(2, "0")}</span>
                  <h2 className="font-display text-2xl md:text-3xl font-bold text-white mt-3 leading-tight">{ind.title}</h2>
                </div>
                <div className="md:col-span-8 grid sm:grid-cols-2 gap-8">
                  <div>
                    <p className="text-xs uppercase tracking-widest text-zinc-500 mb-3">The Challenges</p>
                    <ul className="space-y-2">
                      {ind.challenges.map((c) => (
                        <li key={c} className="text-sm text-zinc-400 leading-relaxed">{c}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-violet-400/80 mb-3">How We Help</p>
                    <p className="text-sm text-zinc-300 leading-relaxed">{ind.help}</p>
                  </div>
                </div>
              </div>
            </FadeIn>
          ))}
        </div>
      </section>

      <section className="py-28 border-t border-white/10 bg-[#0A0A0A]">
        <div className="container-custom text-center max-w-2xl mx-auto">
          <FadeIn>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-4">DON'T SEE YOUR INDUSTRY?</h2>
            <p className="text-zinc-500 mb-8">If your business needs stronger positioning, more customers or better digital systems, let's talk.</p>
            <Link
              to="/contact"
              data-testid="industries-page-talk-to-team-link"
              className="group inline-flex items-center gap-2 bg-white text-black font-bold text-xs tracking-widest uppercase px-8 py-4 hover:bg-violet-500 hover:text-white transition-colors duration-300"
            >
              Talk To Our Team
              <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
